import { apiRequest, incaAccessor } from '@cancercentrum/inca';
import invariant from 'invariant';
import orderBy from 'lodash/orderBy';
import { parseISO } from 'date-fns';
import { createViewModel } from '../../utils/data';
export const sortVdItems = (vdItems)=>{
    return orderBy(vdItems, [
        'id'
    ], [
        'desc'
    ]);
};
// ANROP
export const getRegisterRecordChangedDate = async (rrId)=>{
    const rr = await apiRequest(`/RegisterRecords/${rrId}`);
    return parseISO(rr.updatedDateTime);
};
export const getRegisterRecordInfo = async (rrId)=>{
    const rr = await apiRequest(`/RegisterRecords/${rrId}`);
    const data = await apiRequest(`/RegisterRecords/${rrId}/Data`);
    const df = await apiRequest(`/RegisterRecords/${rrId}/DesignedForm`);
    const metadata = await apiRequest(`/DesignedForms/${df.id}/Metadata`);
    return {
        rr,
        data,
        metadata
    };
};
export const getErrandConnection = async (errandId)=>{
    let connection = {};
    let data = {};
    try {
        connection = await apiRequest(`/Errands/${errandId}/Connection`);
    } catch (err) {
        // eslint-disable-next-line
        console.error(`Misslyckades med hämtning av kopplingsinformation för ärende: ${errandId}.`);
    }
    try {
        data = await apiRequest(`/Errands/${errandId}/Data`);
    } catch (err) {
        // eslint-disable-next-line
        console.error(`Misslyckades med hämtning av data för ärende: ${errandId}.`);
    }
    return {
        errandId,
        ...connection,
        lastContributedToRegisterRecordId: data.lastContributedToRegisterRecordId
    };
};
// funktion för att kolla om ett visst ärende (via ärendeid) är kopplat till en viss registerpost (via registerpostid/eller pk)
export const getErrandConnections = async (errandVdList)=>{
    return await Promise.all(errandVdList.map((errandVdItem)=>getErrandConnection(errandVdItem.data.ERRAND_ID)));
};
// METADATA/ROW hjälpfunktioner
// Traversera alla variabler i ett inca.form.metadata-objekt
const traverseMetadata = (formMetadata, cb)=>{
    const tables = Object.keys(formMetadata);
    tables.forEach((tableName)=>{
        Object.keys(formMetadata[tableName].regvars).forEach((shortname)=>{
            const metadata = formMetadata[tableName].regvars[shortname];
            cb(shortname, metadata, tableName);
        });
    });
};
// jämförelsefunktion för att kontrollera att allt som finns i inca.form.data finns med i angivet registerpostmetadataobjekt
export const getMissingRegvars = (rrMetadata, excludedVariables)=>{
    //TODO: avduplicera lista
    const missing = [];
    // Kolla om någon variabel i formulärets metadata inte finns i registerposten
    traverseMetadata(incaAccessor().form.metadata, (shortname, metadata, tableName)=>{
        if (excludedVariables.includes(shortname)) {
        //return;
        } else if (!rrMetadata[tableName].regvars[shortname]) {
            missing.push(shortname);
        }
    });
    return missing;
};
// funktion för att ta fram exkluderade variabler ur inca.form.data
export const getExcludedVariables = ()=>{
    const excluded = [];
    traverseMetadata(incaAccessor().form.metadata, (shortname, metadata)=>{
        if (metadata.serversideOnly) {
            excluded.push(shortname);
        }
    });
    return excluded;
};
export const clearForm = (originalRow, excludedVars)=>{
    const regvars = Object.keys(originalRow.toIncaJS().regvars);
    const vdlists = Object.keys(originalRow.toIncaJS().vdlists);
    const subTables = Object.keys(originalRow.toIncaJS().subTables);
    let newRow = originalRow;
    newRow = regvars.reduce((accumulatedRow, shortName)=>{
        if (excludedVars.includes(shortName)) {
            return accumulatedRow;
        }
        return accumulatedRow.clearRegvarValue(shortName, {
            washField: true
        });
    }, newRow);
    newRow = vdlists.reduce((accumulatedRow, shortName)=>{
        if (excludedVars.includes(shortName)) {
            return accumulatedRow;
        }
        return accumulatedRow.clearVdlistValue(shortName, {
            washField: true
        });
    }, newRow);
    newRow = subTables.reduce((accumulatedRow, shortName)=>{
        const subTableRows = accumulatedRow.getSubTable(shortName).getRows();
        if (subTableRows.size > 0) {
            return subTableRows.reduce((accumulatedSubTableRow, subTableRow)=>{
                return accumulatedSubTableRow.removeSubTableRow(subTableRow);
            }, accumulatedRow);
        }
        return accumulatedRow;
    }, newRow);
    return newRow;
};
const fillRowRegvars = (dest, source, excludedVars)=>{
    let r = dest;
    Object.keys(dest.regvars.toJS()).forEach((shortname)=>{
        if (!excludedVars.includes(shortname)) {
            r = r.setRegvarValue(shortname, source.getRegvarValue(shortname));
        }
    });
    return r;
};
const fillForm = (destinationRow, sourceRow, vdNames, excludedVars, currentTableName, rootTableName)=>{
    invariant(vdNames[currentTableName], `DirectCompletion: vdName for ${currentTableName} is missing`);
    let r = destinationRow;
    // fyll variabler och vd från source till dest
    r = fillRowRegvars(r, sourceRow, excludedVars);
    const vdName = vdNames[currentTableName];
    if (currentTableName !== rootTableName) {
        r = r.setVdlistValue(vdName, sourceRow.getId());
    }
    const sourceTables = sourceRow.subTables.map((subTable)=>subTable?.$rcc.tableName);
    const destTables = destinationRow.subTables.map((subTable)=>subTable?.$rcc.tableName);
    const tables = sourceTables.filter((sourceTableName)=>destTables.includes(sourceTableName));
    tables.forEach((sourceTableName)=>{
        //@ts-ignore sluta
        sourceRow.getSubTableRows(sourceTableName).forEach((sourceSubRow)=>{
            //@ts-ignore sluta
            let newRow = destinationRow.getSubTable(sourceTableName).createRow();
            //@ts-ignore sluta
            newRow = fillForm(newRow, sourceSubRow, vdNames, excludedVars, sourceTableName, rootTableName);
            r = r.addSubTableRow(newRow);
        });
    });
    return r;
};
export const fillRow = (originalRow, rrData, rrMetadata, vdNames, excludedVars, rootTableName)=>{
    const original = originalRow;
    let filled = clearForm(originalRow, excludedVars);
    const sourceData = createViewModel(rrData, rrMetadata);
    filled = fillForm(filled, sourceData, vdNames, excludedVars, rootTableName, rootTableName);
    return {
        original,
        filled
    };
};
export const formatListItem = (vdFacts, vdItem)=>{
    let listItem = '';
    Object.keys(vdFacts).forEach((title, i, arr)=>{
        const isLastFact = i === arr.length - 1;
        const vdKey = vdFacts[title];
        let key;
        let value;
        let show = true;
        if (typeof vdKey === 'string') {
            key = vdKey;
            value = vdItem.data[key];
        } else {
            key = vdKey.shortname;
            value = vdKey.formatter ? vdKey.formatter(vdItem.data[key], vdItem.data) : vdItem.data[key];
            show = vdKey.show ? vdKey.show(vdItem.data[key], vdItem.data) : true;
        }
        const next = show ? `${title}: ${value ?? '(Ej angivet)'}${isLastFact ? '' : ', '}` : '';
        listItem = listItem + next;
    });
    return listItem.endsWith(', ') ? listItem.slice(0, -2) : listItem;
};
export const capitalize = (s)=>s && s[0].toUpperCase() + s.slice(1);
export const generateListOptions = (tableLevel, formNameParent, rootVdList, subVdList, connectableFormsFormatter, isMainForm)=>{
    if (isMainForm) {
        // Ett formulär på "huvudnivå", t.ex. anmälan, har ingen parent, så vi behöver inte skapa några options.
        return null;
    }
    let opts = [];
    const unconnected = [];
    if (tableLevel === 'ROOT') {
        // Formuläret är ett "subformulär" i rottabellen - skapa hierarki genom att matcha id i "mainpost" mot id i "subpost"
        opts = rootVdList.map((root)=>{
            return {
                text: `${connectableFormsFormatter(root, undefined, true)}`,
                optionFilter: (option)=>option.id === root.id
            };
        });
        subVdList.forEach((sub)=>{
            if (!rootVdList.find((root)=>root.id === sub.id)) {
                // sub är en "fristående" post
                unconnected.push(sub);
            }
        });
        opts.push({
            text: `${capitalize(formNameParent)} ej rapporterad`,
            optionFilter: (option)=>unconnected.find((unc)=>unc.id === option.id)
        });
    } else {
        // Formuläret är ett "subformulär" i rottabellen - skapa hierarki genom att matcha id i "mainpost" mot parent_pk i "subpost"
        opts = rootVdList.map((root)=>{
            return {
                text: `${connectableFormsFormatter(root)}`,
                optionFilter: (option)=>option.data.parent_pk === root.id
            };
        });
        subVdList.forEach((sub)=>{
            if (!rootVdList.find((root)=>root.id === sub.data.parent_pk)) {
                // sub är en "fristående" post
                unconnected.push(sub);
            }
        });
        opts.push({
            text: `Ej kopplad till ${capitalize(formNameParent)}`,
            optionFilter: (option)=>unconnected.find((unc)=>unc.id === option.id)
        });
    }
    return opts;
};
